import React, {useMemo} from 'react';

import {ModuleEnum, TaskDefinitionStateEnum} from 'src/api/ProactApiClient';
import type {DueDatePreviewFormProps} from 'src/app/forms/DueDatePreviewForm';
import {DueDatePreviewForm} from 'src/app/forms/DueDatePreviewForm';
import type {ProactFormApi} from 'src/app/forms/engine/useProactForm';
import {useProactForm} from 'src/app/forms/engine/useProactForm';
import {useGetSimpleComplianceStandardListQuery} from 'src/redux/queries/complianceStandard';
import {useTaskPermissions} from 'src/shared/auth/permissions/useTaskPermissions';
import useSkipTokensByFeature from 'src/shared/hooks/useSkipTokensByFeature';
import type {IFormDataV2, IFormFieldV2} from 'src/types/FormV2';
import {
  ActionFormMetaRule,
  AdminActionsMeta,
  UserActionsMeta,
} from './actionHelpers';

export interface UseActionFormOptions {
  metarules: ActionFormMetaRule[];
  /** Drives the meta variant (e.g. preview due date while in Draft). */
  taskState?: TaskDefinitionStateEnum;
  initialData?: IFormDataV2;
  /** Adjusts the generated metadata (labels, removed fields, ...). */
  transformMeta?: (fields: IFormFieldV2[]) => IFormFieldV2[];
}

export interface UseActionFormResult {
  formApi: ProactFormApi;
  formMeta: IFormFieldV2[];
  isLoading: boolean;
}

export const useActionForm = ({
  metarules,
  taskState = TaskDefinitionStateEnum.Draft,
  initialData,
  transformMeta,
}: UseActionFormOptions): UseActionFormResult => {
  const {skipCompliance} = useSkipTokensByFeature();

  const taskPerms = useTaskPermissions(ModuleEnum.Actions);

  const {
    data: standards,
    isFetching: isStandardsFetching,
    isLoading: isStandardsLoading,
  } = useGetSimpleComplianceStandardListQuery(undefined, {
    skip: skipCompliance,
  });

  const isLoading = isStandardsFetching || isStandardsLoading;

  const useDueInDays = metarules.includes(ActionFormMetaRule.UseDueInDays);
  const useDueDate = metarules.includes(ActionFormMetaRule.UseDueDate);

  const formMeta = useMemo<IFormFieldV2[]>(() => {
    if (isLoading) {
      return [];
    }
    const editTaskMeta = taskPerms.view
      ? AdminActionsMeta(
          taskState,
          standards?.data ?? [],
          useDueInDays,
          useDueDate
        )
      : UserActionsMeta(taskState, useDueInDays, useDueDate);
    return transformMeta ? transformMeta(editTaskMeta) : editTaskMeta;
  }, [
    taskPerms.view,
    isLoading,
    standards?.data,
    taskState,
    useDueInDays,
    useDueDate,
    transformMeta,
  ]);

  const formApi = useProactForm({
    formMeta,
    initialData,
    enablePreSubmissionValidation: false,
  });

  return {formApi, formMeta, isLoading};
};

export type ActionFormProps = DueDatePreviewFormProps;

export const ActionForm: React.FC<ActionFormProps> = props => (
  <DueDatePreviewForm {...props} />
);
